import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, AlertTriangle, RefreshCw, Key, Shield, Pen } from "lucide-react";

// ── Helpers ──────────────────────────────────────────────────

function base64UrlEncode(str: string): string {
    return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function hmacSign(
    data: string,
    secret: string,
    algo: "SHA-256" | "SHA-384" | "SHA-512",
): Promise<string> {
    const enc = new TextEncoder();
    const key = await crypto.subtle.importKey(
        "raw",
        enc.encode(secret),
        { name: "HMAC", hash: algo },
        false,
        ["sign"],
    );
    const sig = await crypto.subtle.sign("HMAC", key, enc.encode(data));
    // Convert to base64url
    const bytes = new Uint8Array(sig);
    let binary = "";
    for (const b of bytes) binary += String.fromCharCode(b);
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

type Algorithm = "HS256" | "HS384" | "HS512";

const ALGO_MAP: Record<Algorithm, "SHA-256" | "SHA-384" | "SHA-512"> = {
    HS256: "SHA-256",
    HS384: "SHA-384",
    HS512: "SHA-512",
};

const DEFAULT_HEADER = JSON.stringify({ alg: "HS256", typ: "JWT" }, null, 2);

const DEFAULT_PAYLOAD = JSON.stringify(
    {
        sub: "1234567890",
        name: "John Doe",
        iat: Math.floor(Date.now() / 1000),
        exp: Math.floor(Date.now() / 1000) + 3600,
    },
    null,
    2,
);

// ── Component ────────────────────────────────────────────────

export function JwtEncoder() {
    const [headerStr, setHeaderStr] = useState(DEFAULT_HEADER);
    const [payloadStr, setPayloadStr] = useState(DEFAULT_PAYLOAD);
    const [secret, setSecret] = useState("your-256-bit-secret");
    const [algorithm, setAlgorithm] = useState<Algorithm>("HS256");
    const [token, setToken] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState<string | null>(null);

    useEffect(() => {
        document.title = "JWT Encoder — Create & Sign JSON Web Tokens — Ballah Code";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute(
                "content",
                "Free JWT encoder. Create and sign JSON Web Tokens with HMAC-SHA256/384/512. Set custom headers, payloads, and secrets. Runs in your browser.",
            );
        }
    }, []);

    // Keep header alg in sync
    useEffect(() => {
        try {
            const hdr = JSON.parse(headerStr);
            if (hdr.alg !== algorithm) {
                setHeaderStr(JSON.stringify({ ...hdr, alg: algorithm }, null, 2));
            }
        } catch {
            // ignore parse errors during editing
        }
    }, [algorithm]);

    const encode = useCallback(async () => {
        setError(null);
        try {
            // Validate JSON
            let header: Record<string, unknown>;
            try {
                header = JSON.parse(headerStr);
            } catch {
                throw new Error("Invalid JSON in header");
            }
            let payload: Record<string, unknown>;
            try {
                payload = JSON.parse(payloadStr);
            } catch {
                throw new Error("Invalid JSON in payload");
            }

            // Ensure alg is set
            if (!header.alg) header.alg = algorithm;
            if (!header.typ) header.typ = "JWT";

            const encodedHeader = base64UrlEncode(JSON.stringify(header));
            const encodedPayload = base64UrlEncode(JSON.stringify(payload));
            const signingInput = `${encodedHeader}.${encodedPayload}`;

            const alg = (header.alg as Algorithm) || algorithm;
            const hashAlgo = ALGO_MAP[alg] || "SHA-256";
            const signature = await hmacSign(signingInput, secret, hashAlgo);

            setToken(`${signingInput}.${signature}`);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Encoding failed");
            setToken("");
        }
    }, [headerStr, payloadStr, secret, algorithm]);

    // Auto-encode on change
    useEffect(() => {
        const timer = setTimeout(encode, 200);
        return () => clearTimeout(timer);
    }, [encode]);

    const copy = async (val: string, key: string) => {
        await navigator.clipboard.writeText(val);
        setCopied(key);
        setTimeout(() => setCopied(null), 2000);
    };

    const insertTimestamps = () => {
        try {
            const payload = JSON.parse(payloadStr);
            payload.iat = Math.floor(Date.now() / 1000);
            payload.exp = Math.floor(Date.now() / 1000) + 3600;
            setPayloadStr(JSON.stringify(payload, null, 2));
        } catch {
            // ignore
        }
    };

    return (
        <div className="min-h-screen bg-background text-foreground font-sans">
            <nav className="flex items-center justify-between px-8 py-[18px]">
                <Link to="/" className="text-sm font-semibold tracking-[-0.03em] text-zinc-200 hover:text-white transition-colors">
                    ballah code
                </Link>
                <div className="flex items-center gap-6 text-[13px] text-zinc-600">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">tools</Link>
                    <Link to="/" className="hover:text-zinc-400 transition-colors">home</Link>
                </div>
            </nav>

            <main className="max-w-[1280px] mx-auto px-8 pt-12 pb-24">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-[13px] text-zinc-600 mb-8">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">Tools</Link>
                    <span className="text-zinc-700">/</span>
                    <span className="text-zinc-400">JWT Encoder</span>
                </div>

                <div className="mb-8">
                    <h1 className="text-3xl font-medium tracking-[-0.03em] mb-3">JWT Encoder</h1>
                    <p className="text-zinc-500 text-[15px] leading-relaxed max-w-2xl">
                        Create and sign JSON Web Tokens. Set your header, payload, and secret — the JWT is generated live.
                        Need to decode a JWT instead?{" "}
                        <Link to="/tools/jwt-decoder" className="text-violet-400 hover:text-violet-300 transition-colors">
                            Use the decoder →
                        </Link>
                    </p>
                </div>

                {/* Algorithm & Secret */}
                <div className="flex items-center gap-3 mb-6 flex-wrap">
                    <div className="flex rounded-lg border border-white/[0.06] overflow-hidden">
                        {(["HS256", "HS384", "HS512"] as const).map((alg) => (
                            <button
                                key={alg}
                                onClick={() => setAlgorithm(alg)}
                                className={`px-3.5 py-2 text-[12px] transition-colors ${algorithm === alg ? "bg-white/[0.08] text-zinc-200" : "text-zinc-600 hover:text-zinc-400"
                                    }`}
                            >
                                {alg}
                            </button>
                        ))}
                    </div>

                    <div className="flex-1 min-w-[200px]">
                        <div className="flex items-center bg-[#111113] border border-white/[0.06] rounded-lg overflow-hidden">
                            <span className="px-3 text-zinc-600"><Key size={13} /></span>
                            <input
                                value={secret}
                                onChange={(e) => setSecret(e.target.value)}
                                placeholder="HMAC secret key"
                                spellCheck={false}
                                className="flex-1 bg-transparent py-2.5 pr-3 text-[13px] text-zinc-300 font-mono outline-none placeholder:text-zinc-800"
                            />
                        </div>
                    </div>

                    <button
                        onClick={insertTimestamps}
                        className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-white/[0.06] text-[12px] text-zinc-500 hover:text-zinc-300 transition-colors"
                    >
                        <RefreshCw size={11} />
                        Refresh iat/exp
                    </button>
                </div>

                {/* Header & Payload side by side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                    {/* Header */}
                    <div className="flex flex-col">
                        <div className="flex items-center justify-between px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                            <div className="flex items-center gap-2">
                                <Shield size={12} className="text-zinc-600" />
                                <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">Header</span>
                            </div>
                        </div>
                        <textarea
                            value={headerStr}
                            onChange={(e) => setHeaderStr(e.target.value)}
                            spellCheck={false}
                            className="w-full min-h-[140px] bg-[#111113] border border-white/[0.06] rounded-b-xl p-4 text-[13px] text-zinc-300 font-mono leading-relaxed outline-none resize-y placeholder:text-zinc-800 focus:border-white/[0.1] transition-colors"
                        />
                    </div>

                    {/* Payload */}
                    <div className="flex flex-col">
                        <div className="flex items-center justify-between px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                            <div className="flex items-center gap-2">
                                <Pen size={12} className="text-zinc-600" />
                                <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">Payload</span>
                            </div>
                        </div>
                        <textarea
                            value={payloadStr}
                            onChange={(e) => setPayloadStr(e.target.value)}
                            spellCheck={false}
                            className="w-full min-h-[140px] bg-[#111113] border border-white/[0.06] rounded-b-xl p-4 text-[13px] text-zinc-300 font-mono leading-relaxed outline-none resize-y placeholder:text-zinc-800 focus:border-white/[0.1] transition-colors"
                        />
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div className="flex items-center gap-2 text-[13px] text-red-400 mb-4 p-3 bg-red-400/[0.05] border border-red-400/10 rounded-xl">
                        <AlertTriangle size={14} /> {error}
                    </div>
                )}

                {/* Generated token */}
                {token && (
                    <div className="mb-16">
                        <div className="flex items-center justify-between px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                            <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">Generated JWT</span>
                            <button
                                onClick={() => copy(token, "token")}
                                className="flex items-center gap-1.5 text-[11px] text-zinc-600 hover:text-zinc-300 transition-colors"
                            >
                                {copied === "token" ? <Check size={11} className="text-green-400" /> : <Copy size={11} />}
                                {copied === "token" ? "Copied" : "Copy"}
                            </button>
                        </div>
                        <div className="bg-[#111113] border border-white/[0.06] rounded-b-xl p-4 overflow-auto">
                            <code className="text-[13px] font-mono break-all leading-relaxed">
                                {(() => {
                                    const parts = token.split(".");
                                    return (
                                        <>
                                            <span className="text-red-400">{parts[0]}</span>
                                            <span className="text-zinc-700">.</span>
                                            <span className="text-violet-400">{parts[1]}</span>
                                            <span className="text-zinc-700">.</span>
                                            <span className="text-cyan-400">{parts[2]}</span>
                                        </>
                                    );
                                })()}
                            </code>
                        </div>
                    </div>
                )}

                {/* SEO */}
                <section className="max-w-2xl border-t border-white/[0.04] pt-12">
                    <h2 className="text-lg font-medium tracking-[-0.02em] mb-4 text-zinc-300">
                        About JWT encoding
                    </h2>
                    <div className="space-y-4 text-[14.5px] text-zinc-500 leading-[1.75]">
                        <p>
                            A JSON Web Token (JWT) consists of three parts: a header specifying the algorithm and token type, a payload containing claims, and a cryptographic signature. This tool creates JWTs signed with HMAC using SHA-256, SHA-384, or SHA-512.
                        </p>
                        <p>
                            The header and payload are Base64URL-encoded, then signed using your secret key. The resulting token can be used for testing authentication flows, debugging API integrations, or learning about JWT structure.
                        </p>
                        <p>
                            Everything runs in your browser using the Web Crypto API — your secret key never leaves your machine. Built by the team behind{" "}
                            <Link to="/" className="text-violet-400 hover:text-violet-300 transition-colors">
                                Ballah Code
                            </Link>.
                        </p>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-2">
                        <span className="text-[12px] text-zinc-700 mr-1 self-center">Related →</span>
                        <Link to="/tools/jwt-decoder" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">JWT Decoder</Link>
                        <Link to="/tools/base64" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Base64 Encode / Decode</Link>
                        <Link to="/tools/hash-generator" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Hash Generator</Link>
                        <Link to="/tools/unix-timestamp" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Unix Timestamp Converter</Link>
                    </div>
                </section>
            </main>

            <footer className="border-t border-white/[0.04] py-5 px-8 flex justify-between max-w-[1280px] mx-auto text-xs text-zinc-800">
                <Link to="/" className="hover:text-zinc-600 transition-colors">ballah code</Link>
                <Link to="/tools" className="hover:text-zinc-600 transition-colors">tools</Link>
                <span>ballah tech</span>
            </footer>
        </div>
    );
}
